import React from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import renderSettingsService from '../service/render-settings.service';
import { TransitionType, VideoRenderSettingsEntity } from '../types/entities';
import { motionDefaults } from '../utils/presets';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import readinessService, { VideoRenderOnReport } from '../service/video-render-readiness.service';
import videoRenderService from '../service/video-render.service';

export type GlobalRenderSettingsFormProps = {
  runId: string;
  storyTarget?: 'short' | 'youtube' | 'other' | string | null;
};

const GlobalRenderSettingsForm: React.FC<GlobalRenderSettingsFormProps> = ({ runId, storyTarget }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedMsg, setSavedMsg] = React.useState<string | null>(null);
  const [m, setM] = React.useState<Partial<VideoRenderSettingsEntity>>({});
  const [report, setReport] = React.useState<VideoRenderOnReport | null>(null);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [enqueueMsg, setEnqueueMsg] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      setSavedMsg(null);
      try {
        const row = await renderSettingsService.getByRunId(runId);
        if (!alive) return;
        if (row) setM(row);
        else {
          const isShort = String(storyTarget || '').toLowerCase() === 'short';
          const defW = isShort ? 1080 : 1920;
          const defH = isShort ? 1920 : 1080;
          setM({
            resolutionWidth: defW,
            resolutionHeight: defH,
            fps: 30,
            videoCodec: 'h264',
            audioCodec: 'aac',
            container: 'mp4',
            videoBitrateKbps: 6000,
            audioBitrateKbps: 192,
            // Subtitles defaults
            defaultSubtitlePreset: 'typewriter-center',
            subtitleMarginBottomPx: 100,
            subtitleSafeAreaPct: 10,
            subtitleFontFamily: 'Georgia',
            subtitleTextColor: '#000000',
            subtitleFontSizePx: 60,
            subtitleBgColor: '#ffd966',
            subtitleStrokeColor: '#f5f5e6',
            subtitleStrokeWidth: 0,
            subtitleShadowColor: '#f5f5e6',
            subtitleShadowOpacity: 50,
            // Waveform defaults
            waveformColor: '#00ffaa',
            waveformOpacity: 60,
            waveformHeightPx: 64,
            waveType: 'freq-bars',
            // Audio / transitions / head-tail
            lufsTarget: -14,
            normalize: true,
            limiter: true,
            transitionType: 'fade',
            transitionDurationMs: 300,
            leadInMs: 0,
            leadOutMs: 0,
          });
        }
      } catch (e: any) {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || 'Ayarlar yüklenemedi');
      } finally {
        if (alive) setLoading(false);
      }
    };
    if (runId) load();
    return () => { alive = false; };
  }, [runId, storyTarget]);

  const handleReadiness = async () => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await readinessService.getReportByRunId(runId);
      setReport(r);
      setReportOpen(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Kontrol başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleEnqueue = async () => {
    if (!runId) return;
    setLoading(true);
    setEnqueueMsg(null);
    try {
      const res = await videoRenderService.enqueueByRunId(runId);
      if (res.accepted) setEnqueueMsg('İş kuyruğa alındı');
      else setEnqueueMsg(res.reason === 'already_queued_or_running' ? 'Bu run için bir iş zaten kuyruğa alınmış veya çalışıyor' : 'Kuyruğa alınamadı');
    } catch (e: any) {
      setEnqueueMsg(e?.response?.data?.message || e?.message || 'Kuyruğa alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const setField = (k: keyof VideoRenderSettingsEntity, v: any) => setM((prev) => ({ ...(prev || {}), [k]: v }));

  const codecs = ['h264','h265'] as const;
  const audioCodecs = ['aac','mp3'] as const;
  const containers = ['mp4','mov','mkv'] as const;
  const transitions: TransitionType[] = ['none','fade','crossfade'];
  const motionPresets = ['none','zoom-in','zoom-out','zoom-in-out','pan-left','pan-right','pan-up','pan-down','kenburns-left-in','kenburns-right-in'] as const;
  const subtitlePresets = ['off','typewriter-left','typewriter-center','karaoke-word'] as const;
  const waveformPresets = ['off','bottom-center','top-center','under-subtitles'] as const;
  const waveTypeOptions = ['waveform','spectrum','freq-bars','cqt','vectorscope','histogram','volume'] as const;
  const commonFonts = [
    'Arial','Helvetica','Times New Roman','Times','Georgia','Verdana','Tahoma','Trebuchet MS','Segoe UI','Calibri',
    'Roboto','Noto Sans','Open Sans','Poppins','Lato','Montserrat','Inter','Source Sans Pro','Courier New','Consolas',
  ] as const;

  const handleSave = async () => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    setSavedMsg(null);
    try {
      const body: Partial<VideoRenderSettingsEntity> = { ...m };
      const res = await renderSettingsService.upsertByRunId(runId, body);
      setM(res);
      setSavedMsg('Ayarlar kaydedildi');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Kaydetme başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Global Render Ayarları</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {savedMsg && <Alert severity="success" sx={{ mb: 1 }}>{savedMsg}</Alert>}

      <Stack spacing={1.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">Defaults</Typography>
          <Tooltip
            placement="right"
            title={
              <span>
                Motion/Subtitle/Waveform: Tüm video için varsayılan davranışlar.<br/>
                Subtitle Text/Size/BG: Altyazı metni rengi, boyutu ve arka planı (global).<br/>
                Chunking: Altyazı parçalama limitleri (kelime/karakter) global uygulanır.
              </span>
            }
          >
            <IconButton size="small" aria-label="Bilgi">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField select label="Motion Preset" size="small" value={m.defaultEffectPreset ?? ''} onChange={(e) => {
            const v = e.target.value;
            setField('defaultEffectPreset', v);
            if (v) setField('defaultEffectOptions', v === 'none' ? { amount: 0, segmentMs: 0, fillMode: 'hold' } : motionDefaults(v as string));
          }} sx={{ minWidth: 220 }}>
            {motionPresets.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField select label="Subtitle Preset" size="small" value={m.defaultSubtitlePreset ?? ''} onChange={(e) => setField('defaultSubtitlePreset', e.target.value)} sx={{ minWidth: 220 }}>
            {subtitlePresets.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField select label="Waveform Preset" size="small" value={m.defaultWaveformPreset ?? ''} onChange={(e) => setField('defaultWaveformPreset', e.target.value)} sx={{ minWidth: 220 }}>
            {waveformPresets.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="Subtitle Text Color" type="color" size="small" value={m.subtitleTextColor ?? '#ffffff'} onChange={(e) => setField('subtitleTextColor', e.target.value)} sx={{ minWidth: 180 }} InputLabelProps={{ shrink: true }} />
          <TextField label="Subtitle Size (px)" type="number" size="small" value={m.subtitleFontSizePx ?? ''} onChange={(e) => setField('subtitleFontSizePx', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 180 }} />
          <TextField label="Subtitle BG" type="color" size="small" value={m.subtitleBgColor ?? '#000000'} onChange={(e) => setField('subtitleBgColor', e.target.value)} sx={{ minWidth: 180 }} InputLabelProps={{ shrink: true }} />
          <TextField label="Max Words" type="number" size="small" value={m.subtitleChunkMaxWords ?? ''} onChange={(e) => setField('subtitleChunkMaxWords', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 160 }} />
          <TextField label="Max Chars" type="number" size="small" value={m.subtitleChunkMaxChars ?? ''} onChange={(e) => setField('subtitleChunkMaxChars', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 160 }} />
        </Stack>
        <Divider />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">Video</Typography>
          <Tooltip
            placement="right"
            title={
              <span>
                Çözünürlük (Width/Height): Videonun piksel boyutu.<br/>
                FPS: Saniyedeki kare sayısı (25/30).<br/>
                Video Codec: Görüntü sıkıştırma türü (h264/h265).<br/>
                Audio Codec: Ses sıkıştırma türü (aac/mp3).<br/>
                Container: Dosya biçimi (mp4/mov/mkv).<br/>
                Bitrate: Video/Audio veri oranı; kalite ve dosya boyutunu etkiler.
              </span>
            }
          >
            <IconButton size="small" aria-label="Bilgi">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="Width" type="number" size="small" value={m.resolutionWidth ?? ''} onChange={(e) => setField('resolutionWidth', e.target.value ? Number(e.target.value) : undefined)} sx={{ minWidth: 140 }} />
          <TextField label="Height" type="number" size="small" value={m.resolutionHeight ?? ''} onChange={(e) => setField('resolutionHeight', e.target.value ? Number(e.target.value) : undefined)} sx={{ minWidth: 140 }} />
          <TextField label="FPS" type="number" size="small" value={m.fps ?? ''} onChange={(e) => setField('fps', e.target.value ? Number(e.target.value) : undefined)} sx={{ minWidth: 120 }} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField select label="Video Codec" size="small" value={m.videoCodec ?? ''} onChange={(e) => setField('videoCodec', e.target.value)} sx={{ minWidth: 160 }}>
            {codecs.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select label="Audio Codec" size="small" value={m.audioCodec ?? ''} onChange={(e) => setField('audioCodec', e.target.value)} sx={{ minWidth: 160 }}>
            {audioCodecs.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select label="Container" size="small" value={m.container ?? ''} onChange={(e) => setField('container', e.target.value)} sx={{ minWidth: 160 }}>
            {containers.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField label="Video Bitrate (kbps)" type="number" size="small" value={m.videoBitrateKbps ?? ''} onChange={(e) => setField('videoBitrateKbps', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
          <TextField label="Audio Bitrate (kbps)" type="number" size="small" value={m.audioBitrateKbps ?? ''} onChange={(e) => setField('audioBitrateKbps', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
        </Stack>

        <Divider />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">Subtitles</Typography>
          <Tooltip
            placement="right"
            title={
              <span>
                Bottom Margin: Altyazının alttan boşluğu (px).<br/>
                Safe Area: Kenarlara yaklaşmaması için güvenli alan (%).<br/>
                Font Family: Yazı tipi ailesi.<br/>
                Stroke Color/Width: Kenar çizgisi rengi/kalınlığı.<br/>
                Shadow Color/Opacity: Gölge rengi/yoğunluğu.<br/>
                Arka Plan (BG): RunDetailPage üstündeki "Global Subtitle Ayarları" bölümünde "Subtitle BG" alanından belirlenir.
              </span>
            }
          >
            <IconButton size="small" aria-label="Bilgi">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="Bottom Margin (px)" type="number" size="small" value={m.subtitleMarginBottomPx ?? ''} onChange={(e) => setField('subtitleMarginBottomPx', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
          <TextField label="Safe Area (%)" type="number" size="small" value={m.subtitleSafeAreaPct ?? ''} onChange={(e) => setField('subtitleSafeAreaPct', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
          <TextField select label="Font Family" size="small" value={m.subtitleFontFamily ?? ''} onChange={(e) => setField('subtitleFontFamily', e.target.value)} sx={{ minWidth: 220 }}>
            {commonFonts.map((f) => (
              <MenuItem key={f} value={f}>
                <span style={{ fontFamily: f }}>{f}</span>
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Stroke Color" type="color" size="small" value={m.subtitleStrokeColor ?? '#000000'} onChange={(e) => setField('subtitleStrokeColor', e.target.value)} sx={{ minWidth: 160 }} InputLabelProps={{ shrink: true }} />
          <TextField label="Stroke Width" type="number" size="small" value={m.subtitleStrokeWidth ?? ''} onChange={(e) => setField('subtitleStrokeWidth', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 160 }} />
          <TextField label="Shadow Color" type="color" size="small" value={m.subtitleShadowColor ?? '#000000'} onChange={(e) => setField('subtitleShadowColor', e.target.value)} sx={{ minWidth: 160 }} InputLabelProps={{ shrink: true }} />
          <TextField label="Shadow Opacity (%)" type="number" size="small" value={m.subtitleShadowOpacity ?? ''} onChange={(e) => setField('subtitleShadowOpacity', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
        </Stack>

        <Box sx={{ mt: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">Önizleme</Typography>
          <Typography variant="body1" sx={{ fontFamily: m.subtitleFontFamily || 'inherit', mt: 0.5 }}>
            Örnek altyazı metni — The quick brown fox jumps over the lazy dog 0123456789
          </Typography>
        </Box>

        <Divider />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">Waveform</Typography>
          <Tooltip
            placement="right"
            title={
              <span>
                Color: Dalga rengini belirler.<br/>
                Opacity: Görünürlük (0-100%).<br/>
                Height: Dalganın dikey yüksekliği (px).
              </span>
            }
          >
            <IconButton size="small" aria-label="Bilgi">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="Color" type="color" size="small" value={m.waveformColor ?? '#00ffaa'} onChange={(e) => setField('waveformColor', e.target.value)} sx={{ minWidth: 160 }} InputLabelProps={{ shrink: true }} />
          <TextField label="Opacity (%)" type="number" size="small" value={m.waveformOpacity ?? ''} onChange={(e) => setField('waveformOpacity', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
          <TextField label="Height (px)" type="number" size="small" value={m.waveformHeightPx ?? ''} onChange={(e) => setField('waveformHeightPx', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
        </Stack>
        {/* Wave Type selection and preview */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
          <TextField
            select
            label="Wave Type"
            size="small"
            value={(m as any).waveType ?? 'waveform'}
            onChange={(e) => setField('waveType' as any, e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {waveTypeOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: { xs: '100%', sm: 320 }, height: 160, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 1 }}>
              <img
                src={`/assets/${((m as any).waveType ?? 'waveform')}.png`}
                alt={(m as any).waveType ?? 'waveform'}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.5'; }}
              />
            </Box>
          </Box>
        </Stack>

        <Divider />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">Audio Mastering</Typography>
          <Tooltip
            placement="right"
            title={
              <span>
                LUFS Target: Hedef ses yüksekliği (ör. -14).<br/>
                Normalize: Ses seviyelerini eşitler.<br/>
                Limiter: Tepe değerleri kırpar, distorsiyonu önler.<br/>
                Per-step Gain: Adım başına ek kazanç (dB).
              </span>
            }
          >
            <IconButton size="small" aria-label="Bilgi">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="LUFS Target" type="number" size="small" value={m.lufsTarget ?? ''} onChange={(e) => setField('lufsTarget', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
          <TextField select label="Normalize" size="small" value={String(m.normalize ?? true)} onChange={(e) => setField('normalize', e.target.value === 'true')} sx={{ minWidth: 160 }}>
            {['true','false'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
          <TextField select label="Limiter" size="small" value={String(m.limiter ?? true)} onChange={(e) => setField('limiter', e.target.value === 'true')} sx={{ minWidth: 160 }}>
            {['true','false'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
          <TextField label="Per-step Gain (dB)" type="number" size="small" value={m.perStepGainDb ?? ''} onChange={(e) => setField('perStepGainDb', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
        </Stack>

        <Divider />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">Transitions</Typography>
          <Tooltip
            placement="right"
            title={
              <span>
                Type: Sahne geçiş türü (none/fade/crossfade).<br/>
                none: Geçiş yok, sahne anında değişir.<br/>
                fade: Sahne kararıp/aydınlanarak geçer (black fade).<br/>
                crossfade: Eski sahne kaybolurken yenisi şeffafça belirir (opacity karışımı).<br/>
                Duration: Geçiş süresi (ms).
              </span>
            }
          >
            <IconButton size="small" aria-label="Bilgi">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField select label="Type" size="small" value={m.transitionType ?? 'fade'} onChange={(e) => setField('transitionType', e.target.value as TransitionType)} sx={{ minWidth: 160 }}>
            {transitions.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Duration (ms)" type="number" size="small" value={m.transitionDurationMs ?? ''} onChange={(e) => setField('transitionDurationMs', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
        </Stack>

        <Divider />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">Global Head/Tail</Typography>
          <Tooltip
            placement="right"
            title={
              <span>
                Lead-in: Videonun başına ek boşluk (ms).<br/>
                Lead-out: Videonun sonuna ek boşluk (ms).
              </span>
            }
          >
            <IconButton size="small" aria-label="Bilgi">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="Lead-in (ms)" type="number" size="small" value={m.leadInMs ?? ''} onChange={(e) => setField('leadInMs', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
          <TextField label="Lead-out (ms)" type="number" size="small" value={m.leadOutMs ?? ''} onChange={(e) => setField('leadOutMs', e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 200 }} />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={handleSave} disabled={loading}>Kaydet</Button>
          <Button variant="outlined" onClick={handleReadiness} disabled={loading}>Video Render Control</Button>
        </Stack>
      </Stack>
    </Paper>

    {/* Readiness Report Dialog */}
    <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Video Render Kontrol Raporu</DialogTitle>
      <DialogContent dividers>
        {!report ? (
          <Typography variant="body2">Rapor bulunamadı.</Typography>
        ) : (
          <Stack spacing={1}>
            <Alert severity={report!.ready ? 'success' : 'warning'}>{report!.summary}</Alert>
            <Typography variant="body2">Sahne: {report!.counts.sceneCount} · TTS Adım: {report!.counts.ttsStepCount}</Typography>
            <Divider />
            {report!.issues.length === 0 ? (
              <Typography variant="body2">Sorun bulunmadı.</Typography>
            ) : (
              <Stack spacing={0.5}>
                {report!.issues.map((it, i) => (
                  <Alert key={i} severity={it.level === 'error' ? 'error' : 'warning'}>
                    <strong>{it.code}</strong> — {it.message}
                  </Alert>
                ))}
              </Stack>
            )}
            {enqueueMsg && <Alert severity="info">{enqueueMsg}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleEnqueue} disabled={!report?.ready || loading}>Videoyu Oluştur</Button>
        <Button onClick={() => setReportOpen(false)}>Kapat</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default GlobalRenderSettingsForm;
